/**
 * Every piece of state that changes what is on screen lives in the URL (§4.1).
 *
 * Three behaviours, each of which the prototype gets wrong by not having a URL at all: a default
 * is never written, so the canonical address of the list screen is `/jurisdictions`; the
 * navigation type is chosen per control, so typing in a search box does not fill the history;
 * and an invalid value falls back and rewrites rather than throwing, because a mistyped shared
 * link should still show the page.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

export interface UrlParam<T extends string> {
  /** The value used, and omitted from the URL, when nothing valid is present. */
  fallback: T;
  /** The closed set of acceptable values. Omit for free text. */
  allowed?: readonly T[];
}

export type UrlSpec = Record<string, UrlParam<string>>;

export type UrlValues<S extends UrlSpec> = { [K in keyof S]: string };

export interface UrlState<S extends UrlSpec> {
  values: UrlValues<S>;
  /** `push` for a tab, a sort or a date. `replace` for a keystroke. */
  set: (patch: Partial<Record<keyof S, string | undefined>>, how?: 'push' | 'replace') => void;
}

export function useUrlState<S extends UrlSpec>(spec: S): UrlState<S> {
  const [params, setParams] = useSearchParams();

  const values = useMemo(() => {
    const resolved = {} as UrlValues<S>;
    for (const [name, rule] of Object.entries(spec)) {
      const raw = params.get(name);
      const valid =
        raw !== null && raw !== '' && (rule.allowed === undefined || rule.allowed.includes(raw));
      resolved[name as keyof S] = valid ? raw : rule.fallback;
    }
    return resolved;
  }, [params, spec]);

  const set = useCallback<UrlState<S>['set']>(
    (patch, how = 'push') => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [name, value] of Object.entries(patch)) {
            const rule = spec[name];
            const resolved = value ?? '';
            if (rule === undefined || resolved === '' || resolved === rule.fallback) {
              next.delete(name);
            } else {
              next.set(name, resolved);
            }
          }
          return next;
        },
        { replace: how === 'replace' },
      );
    },
    [setParams, spec],
  );

  return { values, set };
}

/**
 * Rewrite the address when it carries a value outside the allowed set, so the URL and the screen
 * agree after a mistyped link. Replace, never push: the bad value should not be a history entry.
 */
export function useCanonicalUrl<S extends UrlSpec>(spec: S, state: UrlState<S>): void {
  const [params, setParams] = useSearchParams();
  const drift = Object.entries(spec).some(([name, rule]) => {
    const raw = params.get(name);
    if (raw === null) return false;
    if (raw === '') return true;
    if (raw === rule.fallback) return true;
    return rule.allowed !== undefined && !rule.allowed.includes(raw);
  });

  if (drift) {
    const next = new URLSearchParams(params);
    for (const [name, rule] of Object.entries(spec)) {
      const value = state.values[name as keyof S];
      if (value === rule.fallback) next.delete(name);
      else next.set(name, value);
    }
    queueMicrotask(() => setParams(next, { replace: true }));
  }
}
